import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Alert,
  CircularProgress,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Tabs,
  Tab,
  Table as MuiTable,
} from '@mui/material';
import { Refresh as RefreshIcon, Warning as WarningIcon } from '@mui/icons-material';
import { mt5API } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

// Persists across tab switches (component unmount/remount) for the page session.
// Tracks which deviated account IDs the user has already been warned about.
const acknowledgedDeviationIds = new Set();

export default function AccountStatusTable({ accounts, master, onRefresh, errorAlertIds = new Set(), focusedAccountId, onFastPoll, blinkAll = false }) {
  const { isViewer, canSeeColumn, canViewSetup, unionDashboardColumns } = useAuth();

  const [openWarningDialog, setOpenWarningDialog] = useState(false);
  const [deviatedAccounts, setDeviatedAccounts] = useState([]);
  const rowRefs = useRef({});
  const [fixingIds, setFixingIds] = useState(new Set());
  const [fixingAll, setFixingAll] = useState(false);
  const [algoTogglingIds, setAlgoTogglingIds] = useState(new Set());
  const [algoAllToggling, setAlgoAllToggling] = useState(false);
  const [closingAllPositions, setClosingAllPositions] = useState(false);
  const [closingAllOrders, setClosingAllOrders] = useState(false);
  const [panicClosing, setPanicClosing] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, title: '', message: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [groupFilter, setGroupFilter] = useState('');

  // Header column set = union of effective columns across rendered rows.
  // null = "show every column" (no restriction or at least one row unrestricted).
  // Must be computed AFTER groupFilter is declared.
  const visibleAccounts = (accounts || []).filter(a => !groupFilter || a.group_name === groupFilter);
  const unionCols = unionDashboardColumns(visibleAccounts);
  // Header visibility: column appears if any row uses it.
  const headerShow = (key) => unionCols === null || unionCols.includes(key);
  // Per-row cell visibility (decides whether to render value or em-dash placeholder).
  const cellShow = (key, accountId) => canSeeColumn(key, accountId);
  const [configDialog, setConfigDialog] = useState({ open: false, accountId: null, slave: null, checker: null, master: null, slaveUpdatedAt: null, checkerUpdatedAt: null, masterUpdatedAt: null, loading: false, error: null });
  const [configTab, setConfigTab] = useState(0);
  const [autoFixTogglingIds, setAutoFixTogglingIds] = useState(new Set());

  // Scroll to focused account row and flash highlight when a notification is clicked
  useEffect(() => {
    if (focusedAccountId && rowRefs.current[focusedAccountId]) {
      const el = rowRefs.current[focusedAccountId];
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Restart animation even if already running
      el.classList.remove('row-highlighted');
      void el.offsetWidth; // force reflow
      el.classList.add('row-highlighted');
    }
  }, [focusedAccountId]);

  // SQLite CURRENT_TIMESTAMP is UTC but has no 'Z' suffix — force UTC parsing
  const parseUtcDate = (ts) => new Date(ts.includes('Z') || ts.includes('+') ? ts : ts.replace(' ', 'T') + 'Z');

  const formatMoney = (value, isCent) => {
    const num = parseFloat(value || 0).toFixed(2);
    return isCent ? `${num}¢` : `$${num}`;
  };

  const isDeviated = (accountValue, masterValue) => {
    if (!master || !masterValue) return false;
    return Math.abs(accountValue - masterValue) > 0.001;
  };

  const getRowStyle = (account) => {
    const accountId = String(account.mt5_account_id);
    const isErrorAlert = errorAlertIds.has(accountId);
    const hasError = Number(account.has_error) > 0;

    // has_error (lỗi EA thật) → đổi text cả dòng sang đỏ.
    // Bỏ deviation check vì so sánh slave với singleton `master` không đúng
    // trong setup multi-master (slave-of-master2 luôn lệch với master1).
    return {
      borderLeft: isErrorAlert ? '3px solid #ed6c02' : undefined,
      ...(hasError && {
        '& .MuiTableCell-root': { color: 'red !important' },
      }),
    };
  };

  // Disabled — same reason as getRowStyle: deviation comparison broken
  // in multi-master setup. Always returns empty style.
  const getCellStyle = () => ({});

  // Request notification permission on component mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Function to play alert sound
  const playAlertSound = () => {
    try {
      // Create a simple beep sound using Web Audio API
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 800; // Frequency in Hz
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (error) {
      console.error('Failed to play alert sound:', error);
    }
  };

  // Function to show browser notification
  const showBrowserNotification = (deviations) => {
    // Play alert sound
    playAlertSound();

    if ('Notification' in window && Notification.permission === 'granted') {
      const deviationCount = deviations.length;
      const accountList = deviations.slice(0, 3).map(d => d.accountId).join(', ');
      const moreText = deviations.length > 3 ? ` và ${deviations.length - 3} tài khoản khác` : '';

      const title = '⚠️ Cảnh báo Copy Trade';
      const body = `Phát hiện ${deviationCount} tài khoản có độ lệch với Master!\n` +
                   `Tài khoản: ${accountList}${moreText}\n` +
                   `Nhấn để xem chi tiết.`;

      const notification = new Notification(title, {
        body: body,
        icon: '/favicon.ico',
        tag: 'copy-trade-deviation', // Prevents duplicate notifications
        requireInteraction: true, // Notification stays until user interacts
        badge: '/favicon.ico',
      });

      // When user clicks notification, focus the window and show dialog
      notification.onclick = () => {
        window.focus();
        setOpenWarningDialog(true);
        notification.close();
      };
    }
  };

  // DISABLED: multi-master setup broke deviation comparison.
  //
  // Logic cũ so sánh MỌI slave với một singleton `master` ref, nhưng khi
  // có nhiều master + slave theo từng master khác nhau, slave-of-master2
  // sẽ luôn lệch với master1 → báo nhầm liên tục. Giữ stub để các state/
  // dialog dependencies vẫn compile, nhưng không bao giờ trigger.
  //
  // Re-enable: cần track mapping (slave → master) per-account để so sánh
  // slave với master tương ứng của nó.
  useEffect(() => {
    setDeviatedAccounts([]);
    setOpenWarningDialog(false);
  }, [accounts, master]);

  const handleCloseWarning = () => {
    setOpenWarningDialog(false);
  };

  const handleFixAllErrors = async () => {
    if (!accounts || accounts.length === 0) return;
    setFixingAll(true);
    // Loại master khỏi bulk fix-error (master không có gì để fix vì là source).
    const ids = accounts
      .filter(a => !Number(a.is_master))
      .map(a => String(a.mt5_account_id));
    setFixingIds(new Set(ids));
    let successCount = 0;
    let failCount = 0;
    await Promise.all(ids.map(async (id) => {
      try {
        await mt5API.fixError(id);
        successCount++;
      } catch {
        failCount++;
      } finally {
        setFixingIds(prev => { const next = new Set(prev); next.delete(id); return next; });
      }
    }));
    setFixingAll(false);
    onFastPoll?.();
    const msg = failCount === 0
      ? `Đã gửi lệnh sửa lỗi cho tất cả ${successCount} tài khoản`
      : `Thành công: ${successCount}, Thất bại: ${failCount}`;
    setSnackbar({ open: true, message: msg, severity: failCount === 0 ? 'success' : 'warning' });
  };

  const handleAlgoToggleAll = async (enable) => {
    if (!accounts || accounts.length === 0) return;
    setAlgoAllToggling(true);
    // Bulk actions exclude master accounts — master không bị thao tác hàng loạt
    // (không tắt Algo, không đóng vị thế, không sửa lỗi…). Filter by Number()
    // để cover cả 1 / true / "1" trả về từ SQL.
    const ids = accounts
      .filter(a => !groupFilter || a.group_name === groupFilter)
      .filter(a => !Number(a.is_master))
      .map(a => String(a.mt5_account_id));
    setAlgoTogglingIds(new Set(ids));
    let successCount = 0;
    let failCount = 0;
    await Promise.all(ids.map(async (id) => {
      try {
        await mt5API.algoTrading(id, enable);
        successCount++;
      } catch {
        failCount++;
      } finally {
        setAlgoTogglingIds(prev => { const next = new Set(prev); next.delete(id); return next; });
      }
    }));
    setAlgoAllToggling(false);
    onFastPoll?.();
    const action = enable ? 'bật' : 'tắt';
    const msg = failCount === 0
      ? `Đã ${action} Algo cho tất cả ${successCount} tài khoản`
      : `Thành công: ${successCount}, Thất bại: ${failCount}`;
    setSnackbar({ open: true, message: msg, severity: failCount === 0 ? 'success' : 'warning' });
  };

  const handleAutoFixToggle = async (accountId, enable) => {
    const id = String(accountId);
    setAutoFixTogglingIds(prev => new Set(prev).add(id));
    try {
      await mt5API.autoFix(id, enable);
      onFastPoll?.();
      setSnackbar({ open: true, message: `Đã ${enable ? 'bật' : 'tắt'} tự động sửa cho tài khoản ${id}`, severity: 'success' });
    } catch (error) {
      const msg = error.response?.data?.error || error.message || 'Lỗi không xác định';
      setSnackbar({ open: true, message: `Lỗi: ${msg}`, severity: 'error' });
    } finally {
      setAutoFixTogglingIds(prev => { const next = new Set(prev); next.delete(id); return next; });
    }
  };

  const handleCloseAllPositions = async () => {
    if (!accounts || accounts.length === 0) return;
    setClosingAllPositions(true);
    // Bulk actions exclude master accounts — master không bị thao tác hàng loạt
    // (không tắt Algo, không đóng vị thế, không sửa lỗi…). Filter by Number()
    // để cover cả 1 / true / "1" trả về từ SQL.
    const ids = accounts
      .filter(a => !groupFilter || a.group_name === groupFilter)
      .filter(a => !Number(a.is_master))
      .map(a => String(a.mt5_account_id));
    let successCount = 0;
    let failCount = 0;
    await Promise.all(ids.map(async (id) => {
      try {
        await mt5API.closePositions(id);
        successCount++;
      } catch {
        failCount++;
      }
    }));
    setClosingAllPositions(false);
    onFastPoll?.();
    const msg = failCount === 0
      ? `Đã gửi lệnh đóng vị thế cho tất cả ${successCount} tài khoản`
      : `Thành công: ${successCount}, Thất bại: ${failCount}`;
    setSnackbar({ open: true, message: msg, severity: failCount === 0 ? 'success' : 'warning' });
  };

  const handleCloseAllOrders = async () => {
    if (!accounts || accounts.length === 0) return;
    setClosingAllOrders(true);
    // Bulk actions exclude master accounts — master không bị thao tác hàng loạt
    // (không tắt Algo, không đóng vị thế, không sửa lỗi…). Filter by Number()
    // để cover cả 1 / true / "1" trả về từ SQL.
    const ids = accounts
      .filter(a => !groupFilter || a.group_name === groupFilter)
      .filter(a => !Number(a.is_master))
      .map(a => String(a.mt5_account_id));
    let successCount = 0;
    let failCount = 0;
    await Promise.all(ids.map(async (id) => {
      try {
        await mt5API.closeOrders(id);
        successCount++;
      } catch {
        failCount++;
      }
    }));
    setClosingAllOrders(false);
    onFastPoll?.();
    const msg = failCount === 0
      ? `Đã gửi lệnh xóa lệnh chờ cho tất cả ${successCount} tài khoản`
      : `Thành công: ${successCount}, Thất bại: ${failCount}`;
    setSnackbar({ open: true, message: msg, severity: failCount === 0 ? 'success' : 'warning' });
  };

  const handlePanicClose = async () => {
    if (!accounts || accounts.length === 0) return;
    setPanicClosing(true);
    // Bulk actions exclude master accounts — master không bị thao tác hàng loạt
    // (không tắt Algo, không đóng vị thế, không sửa lỗi…). Filter by Number()
    // để cover cả 1 / true / "1" trả về từ SQL.
    const ids = accounts
      .filter(a => !groupFilter || a.group_name === groupFilter)
      .filter(a => !Number(a.is_master))
      .map(a => String(a.mt5_account_id));
    // CopyTrade_WSCommand_<id>.txt is polled every 5s by the Sync Checker EA and deleted
    // after processing; commands sent within one poll window overwrite each other.
    // Order: AUTO_FIX_OFF (stop EA from re-copying lost positions) → CLOSE_ALL → ALGO_OFF
    // (disables EA entirely). 6s gap between each so each command lands in its own poll
    // cycle. ALGO_OFF must run last because it disables the EA itself.
    const sleep = (ms) => new Promise(r => setTimeout(r, ms));
    await Promise.all(ids.map(id => mt5API.autoFix(id, false).catch(() => {})));
    await sleep(6000);
    await Promise.all(ids.map(id => mt5API.closeAll(id).catch(() => {})));
    await sleep(6000);
    await Promise.all(ids.map(id => mt5API.algoTrading(id, false).catch(() => {})));
    setPanicClosing(false);
    onFastPoll?.();
    setSnackbar({ open: true, message: `Đã gửi lệnh Tắt Auto-fix + Đóng tất cả + Tắt Algo cho ${ids.length} tài khoản`, severity: 'success' });
  };

  const openConfirmDialog = (action, title, message) => {
    setConfirmDialog({ open: true, action, title, message });
  };

  const handleConfirm = async () => {
    const { action } = confirmDialog;
    setConfirmDialog({ open: false, action: null, title: '', message: '' });
    if (action === 'closePositions')       await handleCloseAllPositions();
    else if (action === 'closeOrders')     await handleCloseAllOrders();
    else if (action === 'panicClose')      await handlePanicClose();
    else if (action === 'algoOnAll')       await handleAlgoToggleAll(true);
    else if (action === 'algoOffAll')      await handleAlgoToggleAll(false);
    else if (action === 'fixAll')          await handleFixAllErrors();
  };

  const handleAlgoToggle = async (accountId, enable) => {
    const id = String(accountId);
    setAlgoTogglingIds(prev => new Set(prev).add(id));
    try {
      await mt5API.algoTrading(id, enable);
      onFastPoll?.();
      setSnackbar({ open: true, message: `Đã gửi lệnh ${enable ? 'ALGO_ON' : 'ALGO_OFF'} cho tài khoản ${id}`, severity: 'success' });
    } catch (error) {
      const msg = error.response?.data?.error || error.message || 'Lỗi không xác định';
      setSnackbar({ open: true, message: `Lỗi: ${msg}`, severity: 'error' });
    } finally {
      setAlgoTogglingIds(prev => { const next = new Set(prev); next.delete(id); return next; });
    }
  };

  const handleFixError = async (accountId) => {
    const id = String(accountId);
    setFixingIds(prev => new Set(prev).add(id));
    try {
      await mt5API.fixError(id);
      setSnackbar({ open: true, message: `Đã gửi lệnh sửa lỗi cho tài khoản ${id}`, severity: 'success' });
    } catch (error) {
      const msg = error.response?.data?.error || error.message || 'Lỗi không xác định';
      setSnackbar({ open: true, message: `Lỗi: ${msg}`, severity: 'error' });
    } finally {
      setFixingIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handleOpenConfig = async (accountId) => {
    const id = String(accountId);
    setConfigTab(0);
    setConfigDialog({ open: true, accountId: id, slave: null, checker: null, master: null, slaveUpdatedAt: null, checkerUpdatedAt: null, masterUpdatedAt: null, loading: true, error: null });
    try {
      const res = await mt5API.getEAConfig(id);
      setConfigDialog({
        open: true,
        accountId: id,
        slave: res.data.slave,
        checker: res.data.checker,
        master: res.data.master,
        slaveUpdatedAt: res.data.slave_updated_at,
        checkerUpdatedAt: res.data.checker_updated_at,
        masterUpdatedAt: res.data.master_updated_at,
        loading: false,
        error: null,
      });
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Không tải được config';
      setConfigDialog(prev => ({ ...prev, loading: false, error: msg }));
    }
  };

  return (
    <Box sx={{ width: '100%', overflowX: 'auto' }}>

      <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Lọc theo Group</InputLabel>
          <Select
            value={groupFilter}
            label="Lọc theo Group"
            onChange={(e) => setGroupFilter(e.target.value)}
          >
            <MenuItem value="">-- Tất cả --</MenuItem>
            {[...new Set(accounts.map(a => a.group_name).filter(Boolean))].sort().map(g => (
              <MenuItem key={g} value={g}>{g}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {!isViewer() && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="success"
              size="small"
              disabled={algoAllToggling || accounts.length === 0}
              onClick={() => openConfirmDialog('algoOnAll', 'Xác nhận bật Algo', 'Bạn có chắc muốn BẬT Algo Trading cho tất cả tài khoản không?')}
              startIcon={algoAllToggling ? <CircularProgress size={14} color="inherit" /> : null}
            >
              Bật Algo tất cả
            </Button>
            <Button
              variant="outlined"
              color="warning"
              size="small"
              disabled={algoAllToggling || accounts.length === 0}
              onClick={() => openConfirmDialog('algoOffAll', 'Xác nhận tắt Algo', 'Bạn có chắc muốn TẮT Algo Trading cho tất cả tài khoản không? Các slave sẽ dừng copy lệnh từ Master.')}
              startIcon={algoAllToggling ? <CircularProgress size={14} color="inherit" /> : null}
            >
              Tắt Algo tất cả
            </Button>
            <Button
              variant="contained"
              color="error"
              size="small"
              disabled={fixingAll || accounts.length === 0}
              onClick={() => openConfirmDialog('fixAll', 'Xác nhận sửa lỗi tất cả', 'Bạn có chắc muốn gửi lệnh Sửa lỗi cho tất cả tài khoản không?')}
              startIcon={fixingAll ? <CircularProgress size={14} color="inherit" /> : null}
            >
              Sửa lỗi tất cả
            </Button>
            <Button
              variant="contained"
              color="error"
              size="small"
              disabled={closingAllPositions || accounts.length === 0}
              onClick={() => openConfirmDialog('closePositions', 'Xác nhận đóng vị thế', 'Bạn có chắc muốn đóng toàn bộ vị thế đang mở của tất cả tài khoản không?')}
              startIcon={closingAllPositions ? <CircularProgress size={14} color="inherit" /> : null}
            >
              Đóng vị thế tất cả
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="small"
              disabled={closingAllOrders || accounts.length === 0}
              onClick={() => openConfirmDialog('closeOrders', 'Xác nhận xóa lệnh chờ', 'Bạn có chắc muốn xóa toàn bộ lệnh chờ của tất cả tài khoản không?')}
              startIcon={closingAllOrders ? <CircularProgress size={14} color="inherit" /> : null}
            >
              Xóa lệnh chờ tất cả
            </Button>
            <Button
              variant="contained"
              size="small"
              disabled={panicClosing || accounts.length === 0}
              onClick={() => openConfirmDialog('panicClose', '⚠️ Xác nhận Tắt & Đóng tất cả', 'Thao tác này sẽ (theo thứ tự):\n1. Tắt Auto-fix tất cả tài khoản (chặn EA tự copy lại sau khi đóng)\n2. Chờ 6s → Đóng toàn bộ vị thế + xóa lệnh chờ\n3. Chờ 6s → Tắt Algo Trading\n\nBạn có chắc chắn muốn thực hiện?')}
              startIcon={panicClosing ? <CircularProgress size={14} color="inherit" /> : null}
              sx={{ backgroundColor: '#7b1fa2', '&:hover': { backgroundColor: '#6a1b9a' }, whiteSpace: 'nowrap' }}
            >
              Tắt Algo + Đóng tất cả
            </Button>
          </Box>
        )}
      </Box>

      <TableContainer sx={{ maxHeight: { xs: 'calc(100vh - 300px)', sm: 'calc(100vh - 350px)' }, overflowX: 'auto' }}>
        <Table size="small" stickyHeader sx={{ minWidth: isViewer() ? 1020 : 1180, tableLayout: 'fixed' }}>
          <TableHead>
            {(() => {
              const hBase = { backgroundColor: 'rgb(15, 21, 53)', color: 'white', zIndex: 1, padding: '4px 2px', lineHeight: 1.2 };
              const hStack = (label, color) => (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
                  <span style={{ fontSize: '0.62rem', color: 'white' }}>{label}</span>
                  <span style={{ fontSize: '0.7rem', fontWeight: 'bold', color }}>{color === '#4fc3f7' ? 'B' : 'S'}</span>
                </Box>
              );
              return (
                <TableRow>
                  <TableCell align="center" sx={{ ...hBase, width: 30 }}>STT</TableCell>
                  <TableCell align="center" sx={{ ...hBase, width: 82 }}>Tài khoản</TableCell>
                  {headerShow('name') && <TableCell align="center" sx={{ ...hBase, width: 82 }}>Tên</TableCell>}
                  {headerShow('balance') && <TableCell align="center" sx={{ ...hBase, width: 72 }}>Balance</TableCell>}
                  {headerShow('floating') && <TableCell align="center" sx={{ ...hBase, width: 62 }}>Vị thế</TableCell>}
                  {headerShow('closed') && <TableCell align="center" sx={{ ...hBase, width: 62 }}>Đã chốt</TableCell>}
                  {headerShow('total_profit') && <TableCell align="center" sx={{ ...hBase, width: 72 }}>Tổng LN</TableCell>}
                  {headerShow('buy_positions') && <TableCell align="center" sx={{ ...hBase, width: 34 }}>{hStack('Vị thế', '#4fc3f7')}</TableCell>}
                  {headerShow('buy_lots') && <TableCell align="center" sx={{ ...hBase, width: 38 }}>{hStack('Lot', '#4fc3f7')}</TableCell>}
                  {headerShow('sell_positions') && <TableCell align="center" sx={{ ...hBase, width: 34 }}>{hStack('Vị thế', '#ef9a9a')}</TableCell>}
                  {headerShow('sell_lots') && <TableCell align="center" sx={{ ...hBase, width: 38 }}>{hStack('Lot', '#ef9a9a')}</TableCell>}
                  {headerShow('buy_pending') && <TableCell align="center" sx={{ ...hBase, width: 34 }}>{hStack('Chờ', '#4fc3f7')}</TableCell>}
                  {headerShow('buy_pending_lots') && <TableCell align="center" sx={{ ...hBase, width: 38 }}>{hStack('LChờ', '#4fc3f7')}</TableCell>}
                  {headerShow('sell_pending') && <TableCell align="center" sx={{ ...hBase, width: 34 }}>{hStack('Chờ', '#ef9a9a')}</TableCell>}
                  {headerShow('sell_pending_lots') && <TableCell align="center" sx={{ ...hBase, width: 38 }}>{hStack('LChờ', '#ef9a9a')}</TableCell>}
                  {headerShow('group') && <TableCell align="center" sx={{ ...hBase, width: 72 }}>Group</TableCell>}
                  {headerShow('copy_ratio') && <TableCell align="center" sx={{ ...hBase, width: 44 }}>Tỷ lệ</TableCell>}
                  {headerShow('max_loss') && <TableCell align="center" sx={{ ...hBase, width: 54 }}>SL cứng</TableCell>}
                  {headerShow('status') && <TableCell align="center" sx={{ ...hBase, width: 58 }}>T.thái</TableCell>}
                  {!isViewer() && <TableCell align="center" sx={{ ...hBase, width: 54 }}>Algo</TableCell>}
                  {!isViewer() && <TableCell align="center" sx={{ ...hBase, width: 58 }}>Tự sửa</TableCell>}
                  {headerShow('updated_at') && <TableCell align="center" sx={{ ...hBase, width: 56 }}>Cập nhật</TableCell>}
                  {!isViewer() && <TableCell align="center" sx={{ ...hBase, width: 48 }}>Sửa lỗi</TableCell>}
                </TableRow>
              );
            })()}
          </TableHead>
          <TableBody>
            {accounts.filter(a => !groupFilter || a.group_name === groupFilter).length === 0 ? (
              <TableRow>
                <TableCell colSpan={100} align="center">
                  Không có dữ liệu
                </TableCell>
              </TableRow>
            ) : (
              accounts.filter(a => !groupFilter || a.group_name === groupFilter)
                .slice().sort((a, b) => {
                  // Master luôn ở đầu danh sách, sau đó alphabetical theo tên VN.
                  const am = !!Number(a.is_master);
                  const bm = !!Number(b.is_master);
                  if (am !== bm) return am ? -1 : 1;
                  return (a.client_name || '').localeCompare(b.client_name || '', 'vi');
                })
                .map((account, index) => (
                <TableRow
                  key={account.id}
                  ref={el => { if (el) rowRefs.current[String(account.mt5_account_id)] = el; }}
                  sx={{
                    ...getRowStyle(account),
                    // V4.5: cảnh báo kỷ luật → nhấp nháy đỏ TOÀN BỘ các dòng (master + slave)
                    ...(blinkAll ? {
                      animation: 'disciplineRowBlink 1s linear infinite',
                      '@keyframes disciplineRowBlink': {
                        '0%, 100%': { backgroundColor: 'transparent' },
                        '50%': { backgroundColor: 'rgba(239, 68, 68, 0.22)' },
                      },
                    } : {}),
                  }}
                  onClick={e => e.currentTarget.classList.remove('row-highlighted')}
                >
                  <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{index + 1}</TableCell>
                  {(() => {
                    const setupOpen = canViewSetup(account.mt5_account_id);
                    return (
                      <TableCell
                        sx={{
                          fontSize: '0.75rem',
                          color: setupOpen ? 'primary.main' : 'text.primary',
                          cursor: setupOpen ? 'pointer' : 'default',
                          textDecoration: setupOpen ? 'underline' : 'none',
                          ...(setupOpen ? { '&:hover': { color: 'primary.dark' } } : {}),
                        }}
                        onClick={setupOpen
                          ? (e) => { e.stopPropagation(); handleOpenConfig(account.mt5_account_id); }
                          : undefined}
                      >
                        {account.mt5_account_id}
                      </TableCell>
                    );
                  })()}
                  {(() => {
                    // Per-row cell visibility for this account row. Captures the
                    // common "render value vs '—' placeholder" pattern below.
                    const cs = (key) => cellShow(key, account.mt5_account_id);
                    const HIDDEN = '—';
                    return (
                      <>
                        {headerShow('name') && (
                          <TableCell sx={{ fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {cs('name') ? (
                              <>
                                {!!Number(account.is_master) && (
                                  <Box
                                    component="span"
                                    sx={{
                                      display: 'inline-block',
                                      mr: 0.5,
                                      px: 0.45,
                                      borderRadius: 0.4,
                                      fontSize: '0.6rem',
                                      fontWeight: 700,
                                      lineHeight: 1.4,
                                      bgcolor: '#f59e0b',
                                      color: '#fff',
                                      verticalAlign: 'super',
                                    }}
                                    title="Master account"
                                  >
                                    M
                                  </Box>
                                )}
                                {account.client_name || '-'}
                              </>
                            ) : HIDDEN}
                          </TableCell>
                        )}
                        {headerShow('balance') && (
                          <TableCell align="right" sx={{ fontSize: '0.75rem' }}>
                            {cs('balance') ? formatMoney(account.balance, account.is_cent) : HIDDEN}
                          </TableCell>
                        )}
                        {headerShow('floating') && (
                          <TableCell
                            align="right"
                            sx={{
                              // Neutral color when hidden so the dash doesn't leak the value's sign.
                              color: cs('floating') ? (account.open_positions >= 0 ? 'green' : 'red') : 'text.disabled',
                              fontWeight: 'bold',
                              fontSize: '0.75rem',
                            }}
                          >
                            {cs('floating') ? formatMoney(account.open_positions, account.is_cent) : HIDDEN}
                          </TableCell>
                        )}
                        {headerShow('closed') && (
                          <TableCell
                            align="right"
                            sx={{
                              color: cs('closed') ? (account.closed_profit >= 0 ? 'green' : 'red') : 'text.disabled',
                              fontWeight: 'bold',
                              fontSize: '0.75rem',
                            }}
                          >
                            {cs('closed') ? formatMoney(account.closed_profit, account.is_cent) : HIDDEN}
                          </TableCell>
                        )}
                        {headerShow('total_profit') && (
                          <TableCell
                            align="right"
                            sx={{
                              color: cs('total_profit') ? (account.total_profit >= 0 ? 'green' : 'red') : 'text.disabled',
                              fontWeight: 'bold',
                              fontSize: '0.75rem',
                            }}
                          >
                            {cs('total_profit') ? formatMoney(account.total_profit, account.is_cent) : HIDDEN}
                          </TableCell>
                        )}
                        {headerShow('buy_positions') && (
                          <TableCell align="right" sx={{ fontSize: '0.75rem', color: '#1565c0' }}>
                            {cs('buy_positions') ? (account.buy_positions_count || 0) : HIDDEN}
                          </TableCell>
                        )}
                        {headerShow('buy_lots') && (
                          <TableCell align="right" sx={{ fontSize: '0.75rem', color: '#1565c0' }}>
                            {cs('buy_lots') ? parseFloat(account.buy_lots || 0).toFixed(2) : HIDDEN}
                          </TableCell>
                        )}
                        {headerShow('sell_positions') && (
                          <TableCell align="right" sx={{ fontSize: '0.75rem', color: '#c62828' }}>
                            {cs('sell_positions') ? (account.sell_positions_count || 0) : HIDDEN}
                          </TableCell>
                        )}
                        {headerShow('sell_lots') && (
                          <TableCell align="right" sx={{ fontSize: '0.75rem', color: '#c62828' }}>
                            {cs('sell_lots') ? parseFloat(account.sell_lots || 0).toFixed(2) : HIDDEN}
                          </TableCell>
                        )}
                        {headerShow('buy_pending') && (
                          <TableCell align="right" sx={{ fontSize: '0.75rem', color: '#1565c0' }}>
                            {cs('buy_pending') ? (account.buy_pending_count || 0) : HIDDEN}
                          </TableCell>
                        )}
                        {headerShow('buy_pending_lots') && (
                          <TableCell align="right" sx={{ fontSize: '0.75rem', color: '#1565c0' }}>
                            {cs('buy_pending_lots') ? parseFloat(account.buy_pending_lots || 0).toFixed(2) : HIDDEN}
                          </TableCell>
                        )}
                        {headerShow('sell_pending') && (
                          <TableCell align="right" sx={{ fontSize: '0.75rem', color: '#c62828' }}>
                            {cs('sell_pending') ? (account.sell_pending_count || 0) : HIDDEN}
                          </TableCell>
                        )}
                        {headerShow('sell_pending_lots') && (
                          <TableCell align="right" sx={{ fontSize: '0.75rem', color: '#c62828' }}>
                            {cs('sell_pending_lots') ? parseFloat(account.sell_pending_lots || 0).toFixed(2) : HIDDEN}
                          </TableCell>
                        )}
                        {headerShow('group') && (
                          <TableCell sx={{ fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {cs('group') ? (account.group_name || '-') : HIDDEN}
                          </TableCell>
                        )}
                        {headerShow('copy_ratio') && (
                          <TableCell sx={{ fontSize: '0.75rem' }}>
                            {cs('copy_ratio') ? (account.copy_ratio || '-') : HIDDEN}
                          </TableCell>
                        )}
                        {headerShow('max_loss') && (
                          <TableCell align="right" sx={{ fontSize: '0.75rem' }}>
                            {cs('max_loss')
                              ? (account.is_master
                                  ? (() => {
                                      const fx = parseFloat(account.master_sl_fixed) || 0;
                                      const pc = parseFloat(account.master_sl_percent) || 0;
                                      const parts = [];
                                      if (fx > 0) parts.push(formatMoney(fx, account.is_cent));
                                      if (pc > 0) parts.push(`${pc.toFixed(2).replace(/\.?0+$/, '')}%`);
                                      return parts.length ? parts.join(' / ') : '-';
                                    })()
                                  : (account.max_loss != null && account.max_loss !== 0
                                      ? formatMoney(account.max_loss, account.is_cent)
                                      : '-'))
                              : HIDDEN}
                          </TableCell>
                        )}
                        {headerShow('status') && (
                          <TableCell align="center">
                            {cs('status') ? (
                              <Chip
                                label={account.is_running ? 'Chạy' : 'Dừng'}
                                color={account.is_running ? 'success' : 'default'}
                                size="small"
                                sx={{ fontSize: '0.6rem', height: 18 }}
                              />
                            ) : HIDDEN}
                          </TableCell>
                        )}
                      </>
                    );
                  })()}
                  {!isViewer() && (
                    <TableCell align="center">
                      {account.is_master ? (
                        <Box component="span" sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>—</Box>
                      ) : (
                        <Tooltip title={account.is_running ? 'Tắt Algo Trading' : 'Bật Algo Trading'}>
                          <span>
                            <Switch
                              size="small"
                              checked={!!account.is_running}
                              disabled={algoTogglingIds.has(String(account.mt5_account_id))}
                              onChange={(e) => handleAlgoToggle(account.mt5_account_id, e.target.checked)}
                              color="success"
                            />
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  )}
                  {!isViewer() && (
                    <TableCell align="center">
                      {account.is_master ? (
                        <Box component="span" sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>—</Box>
                      ) : (
                        <Tooltip title={account.auto_fix_enabled
                          ? 'Tắt tự động sửa (bot chỉ cảnh báo, chờ thao tác thủ công)'
                          : 'Bật tự động sửa (bot tự đóng thừa / copy thiếu)'}>
                          <span>
                            <Switch
                              size="small"
                              checked={!!account.auto_fix_enabled}
                              disabled={autoFixTogglingIds.has(String(account.mt5_account_id))}
                              onChange={(e) => handleAutoFixToggle(account.mt5_account_id, e.target.checked)}
                              color="info"
                            />
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  )}
                  {headerShow('updated_at') && (
                    <TableCell align="center">
                      {cellShow('updated_at', account.mt5_account_id) ? (
                        <Tooltip title={account.last_updated ? parseUtcDate(account.last_updated).toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' }) : '-'}>
                          <span style={{ fontSize: '0.7rem' }}>
                            {account.last_updated
                              ? parseUtcDate(account.last_updated).toLocaleTimeString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })
                              : '-'
                            }
                          </span>
                        </Tooltip>
                      ) : '—'}
                    </TableCell>
                  )}
                  {!isViewer() && (
                    <TableCell align="center">
                      {account.is_master ? (
                        <Box component="span" sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>—</Box>
                      ) : (
                        <Button
                          variant="contained"
                          color="error"
                          size="small"
                          disabled={fixingIds.has(String(account.mt5_account_id))}
                          onClick={() => handleFixError(account.mt5_account_id)}
                          sx={{ minWidth: 0, fontSize: '0.65rem', whiteSpace: 'nowrap', px: 0.5 }}
                        >
                          {fixingIds.has(String(account.mt5_account_id))
                            ? <CircularProgress size={14} color="inherit" />
                            : 'Sửa lỗi'
                          }
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Warning Dialog */}
      <Dialog
        open={openWarningDialog}
        onClose={handleCloseWarning}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <WarningIcon color="error" />
          Cảnh báo độ lệch Copy Trade
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Phát hiện độ lệch giữa tài khoản Master và các tài khoản Copy. Vui lòng kiểm tra!
          </Alert>

          {/* Notification Status */}
          {'Notification' in window && Notification.permission !== 'granted' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              💡 Cho phép thông báo trình duyệt để nhận cảnh báo ngay cả khi đang ẩn cửa sổ.
              {Notification.permission === 'denied' && (
                <Box sx={{ mt: 1, fontSize: '0.875rem' }}>
                  Bạn đã từ chối quyền thông báo. Vào cài đặt trình duyệt để bật lại.
                </Box>
              )}
            </Alert>
          )}

          {(() => {
            // Lookup master name từ list accounts (master không có name field
            // riêng trong masterData query). Fallback "—" nếu chưa load xong.
            const masterAcc = master ? accounts.find(a => String(a.mt5_account_id) === String(master.mt5_account_id)) : null;
            const masterName = masterAcc?.client_name || '—';
            return (
              <Box sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Box sx={{ mb: 0.5 }}>
                  <strong>Master Account:</strong>{' '}
                  <Box component="span" sx={{ color: 'text.primary' }}>
                    {master?.mt5_account_id} — {masterName}
                  </Box>
                </Box>
                <Box sx={{ mb: 0.5 }}>
                  <strong>Số vị thế mở:</strong> {master?.open_positions_count}
                </Box>
                <Box>
                  <strong>Số lot mở:</strong> {master?.open_lots}
                </Box>
              </Box>
            );
          })()}

          <List>
            {deviatedAccounts.map((dev, index) => (
              <ListItem
                key={index}
                sx={{
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  borderBottom: index < deviatedAccounts.length - 1 ? '1px solid #e0e0e0' : 'none',
                  pb: 2,
                  mb: 2
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ fontWeight: 'bold', mb: 1 }}>
                      Tài khoản: {dev.accountId} ({dev.clientName})
                    </Box>
                  }
                  secondary={
                    <Box>
                      {dev.positionDeviation && (
                        <Box sx={{ mb: 1 }}>
                          <span style={{ color: 'red', fontWeight: 'bold' }}>
                            ⚠ Số vị thế mở:
                          </span>
                          {' '}
                          Tài khoản có <strong>{dev.positionDeviation.account}</strong> vị thế,
                          Master có <strong>{dev.positionDeviation.master}</strong> vị thế
                          {' '}
                          (Chênh lệch: <span style={{ color: 'red', fontWeight: 'bold' }}>
                            {dev.positionDeviation.diff > 0 ? '+' : ''}{dev.positionDeviation.diff}
                          </span>)
                        </Box>
                      )}
                      {dev.lotDeviation && (
                        <Box>
                          <span style={{ color: 'red', fontWeight: 'bold' }}>
                            ⚠ Số lot mở:
                          </span>
                          {' '}
                          Tài khoản có <strong>{dev.lotDeviation.account}</strong> lot,
                          Master có <strong>{dev.lotDeviation.master}</strong> lot
                          {' '}
                          (Chênh lệch: <span style={{ color: 'red', fontWeight: 'bold' }}>
                            {dev.lotDeviation.diff > 0 ? '+' : ''}{dev.lotDeviation.diff}
                          </span>)
                        </Box>
                      )}
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseWarning} variant="contained" color="primary">
            Đã hiểu
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation dialog for destructive all-account actions */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog(d => ({ ...d, open: false }))}>
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>{confirmDialog.message}</DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(d => ({ ...d, open: false }))}>Hủy</Button>
          <Button onClick={handleConfirm} color="error" variant="contained" autoFocus>Xác nhận</Button>
        </DialogActions>
      </Dialog>

      {/* EA Config dialog — Slave + Checker settings for an account */}
      <Dialog
        open={configDialog.open}
        onClose={() => setConfigDialog(d => ({ ...d, open: false }))}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          Cấu hình EA — Tài khoản #{configDialog.accountId}
        </DialogTitle>
        <Tabs
          value={configTab}
          onChange={(_, v) => setConfigTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab label="Copy Slave" />
          <Tab label="Sync Checker" />
          <Tab label="Copy Master" />
        </Tabs>
        <DialogContent sx={{ minHeight: 360 }}>
          {configDialog.loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {configDialog.error && !configDialog.loading && (
            <Alert severity="error" sx={{ my: 2 }}>{configDialog.error}</Alert>
          )}
          {!configDialog.loading && !configDialog.error && configTab === 0 && (
            <EAConfigPanel config={configDialog.slave} updatedAt={configDialog.slaveUpdatedAt} eaLabel="Copy Slave V4.2" />
          )}
          {!configDialog.loading && !configDialog.error && configTab === 1 && (
            <EAConfigPanel config={configDialog.checker} updatedAt={configDialog.checkerUpdatedAt} eaLabel="Sync Checker V4" />
          )}
          {!configDialog.loading && !configDialog.error && configTab === 2 && (
            <EAConfigPanel config={configDialog.master} updatedAt={configDialog.masterUpdatedAt} eaLabel="Copy Master V4.2" />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfigDialog(d => ({ ...d, open: false }))}>Đóng</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// Small helper: render a config object as a two-column key/value table
function EAConfigPanel({ config, updatedAt, eaLabel }) {
  if (!config) {
    return (
      <Alert severity="info" sx={{ my: 2 }}>
        Chưa có dữ liệu config cho {eaLabel}. Biên dịch lại EA và attach vào chart để ghi config mới.
      </Alert>
    );
  }

  // Pretty labels for known keys — fallback to raw key for anything unlisted
  const LABELS = {
    ea_type: 'Loại EA',
    account_id: 'Account ID',
    ea_version: 'Phiên bản EA',
    updated_at: 'Cập nhật (Unix)',
    leverage: 'Tỷ lệ đòn bẩy',
    master_id: 'Master ID', slave_id: 'Slave ID',
    master_name: 'Master Name', slave_name: 'Slave Name',
    check_interval_ms: 'Interval kiểm tra (ms)', check_interval: 'Interval kiểm tra (s)',
    log_enabled: 'Bật log', use_event_log: 'Dùng event log',
    use_balance_ratio: 'Dùng tỷ lệ balance', lot_multiplier: 'Hệ số lot',
    rounding_mode: 'Chế độ làm tròn', use_min_lot: 'Dùng min lot',
    max_lot_per_trade: 'Max lot / lệnh', close_on_min_lot_partial: 'Đóng ở min lot khi partial',
    symbol_prefix: 'Prefix symbol', symbol_suffix: 'Suffix symbol',
    remove_prefix: 'Xóa prefix', remove_suffix: 'Xóa suffix',
    custom_mapping: 'Mapping tùy chỉnh',
    slippage: 'Slippage (points)', magic_number: 'Magic number',
    copy_sl: 'Copy SL', copy_tp: 'Copy TP',
    max_retries: 'Max retries', retry_delay_ms: 'Retry delay (ms)',
    target_profit: 'Mục tiêu lợi nhuận', max_loss: 'Lỗ tối đa',
    enable_daily_reset: 'Reset hàng ngày', reset_hour_gmt: 'Giờ reset (GMT)',
    enable_alert: 'Bật alert', startup_mode: 'Chế độ khởi động',
    auto_fix_enabled: 'Tự sửa lệch', auto_fix_interval: 'Interval tự sửa (s)',
    signal_max_age_sec: 'Tuổi tối đa signal (s)', auto_fix_cooldown: 'Cooldown tự sửa (s)',
    use_magic_filter: 'Lọc theo magic', min_free_margin: 'Free margin tối thiểu',
    grace_period: 'Grace period (s)', check_pending_orders: 'Kiểm tra pending',
    ignore_min_lot_close: 'Bỏ qua min lot close',
    enable_telegram: 'Bật Telegram', telegram_has_token: 'Có Telegram token', telegram_has_chat_id: 'Có chat ID',
    telegram_cooldown: 'Telegram cooldown (s)', enable_telegram_control: 'Bật điều khiển Telegram',
    telegram_poll_interval: 'Telegram poll interval (s)',
    report_has_chat_id: 'Có chat ID báo cáo',
    enable_weekly_report: 'Báo cáo tuần', enable_monthly_report: 'Báo cáo tháng',
    weekly_report_day: 'Ngày báo cáo tuần', report_hour: 'Giờ báo cáo', report_minute: 'Phút báo cáo',
    master_profit_share: '% LN chia master', master_account_no: 'Số TK master', master_email: 'Email master',
    monitor_slave_risk: 'Theo dõi risk slave', balance_snapshot_hour: 'Giờ snapshot balance',
    check_trading_hours: 'Kiểm tra giờ trade', trading_start_hour: 'Giờ bắt đầu', trading_end_hour: 'Giờ kết thúc',
    heartbeat_timeout: 'Heartbeat timeout (s)', slave_heartbeat_timeout: 'Slave heartbeat timeout (s)',
    disconnect_grace_period: 'Grace period mất kết nối (s)',
    auto_fix_mismatch: 'Tự động sửa lệch',
  };

  const formatValue = (k, v) => {
    if (v === true) return '✅';
    if (v === false) return '❌';
    if (v === null || v === undefined || v === '') return '-';
    if (k === 'leverage' && Number(v) > 0) return `1:${v}`;
    return String(v);
  };

  const entries = Object.entries(config).filter(([k]) => k !== 'ea_type' && k !== 'account_id');

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <strong>{eaLabel}</strong>
        {updatedAt && (
          <Box sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
            Cập nhật: {new Date(updatedAt.includes('Z') ? updatedAt : updatedAt.replace(' ', 'T') + 'Z').toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' })}
          </Box>
        )}
      </Box>
      <MuiTable size="small">
        <TableBody>
          {entries.map(([k, v]) => (
            <TableRow key={k}>
              <TableCell sx={{ fontWeight: 500, width: '45%', color: 'text.secondary', fontSize: '0.82rem' }}>
                {LABELS[k] || k}
              </TableCell>
              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.82rem', wordBreak: 'break-all' }}>
                {formatValue(k, v)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </MuiTable>
    </Box>
  );
}
